import * as React from "react";
import * as Actions from "../actions";
import { EventTypeSelector } from "./EventTypeSelector";
import { DeleteEventButton } from "./DeleteEventButton";
import { EventAddInputs } from "./EventAddInputs";
import { AddEventButton } from "./AddEventButton";
import { EditModeEnum, AnalyseState, PlayStateEnum, WaveformType } from "../interfaces/state";
import { connect, Dispatch } from "react-redux";
import { PeaksJs, PeaksJsTime, PeaksPeriod, PeaksPoint } from "../interfacesPeaks/PeaksJS";
import throttle from "lodash-es/throttle";
import { PermissionEnum } from "../../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { useCoughMarshalApiClient } from "../../../misc/api/CoughMarshalApiClient";
import { StudyCoughEventType } from "webportalclient";

export interface EventActionDashboardProps {
    sessionId: string;
    isViewOnly: boolean;
    isEditMode: EditModeEnum;
    areKeyboardShortcutsVisible: boolean;
    availableEventTypes: StudyCoughEventType[];
    selectedEventType: StudyCoughEventType;
    selectedWaveform: string;
    isPlaying: boolean;
    peaksJs: PeaksJs;
    currentWaveformDuration: number;
    dispatch: Dispatch<AnalyseState>;
    isAcceptabilityModalDisplayed: boolean;
    primaryAnalysisSetId: string;
    positionsLoaded: boolean;
    selectedSegment: number;
}

export const EventActionDashboard = ({ sessionId, isViewOnly, isEditMode, areKeyboardShortcutsVisible, availableEventTypes, selectedEventType, selectedWaveform, isPlaying, peaksJs, currentWaveformDuration, dispatch, isAcceptabilityModalDisplayed, primaryAnalysisSetId, positionsLoaded, selectedSegment }: EventActionDashboardProps) => {
    const [isStartPositionValid, setIsStartPositionValid] = React.useState(true);
    const [isEndPositionValid, setIsEndPositionValid] = React.useState(true);
    const [startTime, setStartTime] = React.useState<number>(0);
    const [endTime, setEndTime] = React.useState<number>(0);
    const [isSegmented, setIsSegmented] = React.useState<boolean>(selectedEventType?.period);
    const [isEditing, setIsEditing] = React.useState<boolean>(isEditMode !== EditModeEnum.NotEditing);
    const [selectedSessionWaveformType, setSelectedSessionWaveformType] = React.useState<string>();
    const client = useWebPortalApiClient();
    const [isQCWaveform, setIsQCWaveform] = React.useState<boolean>();
    const [hasAddEventPermissionOnAllWaveforms, setHasAddEventPermissionOnAllWaveforms] = React.useState(false);
    const { currentUserPermissions } = useSecurityContext();
    const coughMarshalClient = useCoughMarshalApiClient();
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const checkQCWaveform = async () => {

            if (currentUserPermissions.some(x => (x.Id === PermissionEnum.AddEventOnAnyWaveform))) {
                setHasAddEventPermissionOnAllWaveforms(true);
            }

            const primaryAnalysisSet = await client.client.getCoughAnalysisSet(primaryAnalysisSetId);
            const selectedSessionWaveform = primaryAnalysisSet.sessionWaveform;
            setSelectedSessionWaveformType(selectedSessionWaveform);

            if (selectedWaveform === selectedSessionWaveform) {
                setIsQCWaveform(true);
            }
            else {
                setIsQCWaveform(false);
            }

            if ((selectedWaveform === selectedSessionWaveform) || hasAddEventPermissionOnAllWaveforms) {
                dispatch(Actions.UpdateAddEventByDoubleClickStateAction.create(true))
            }
            else {
                dispatch(Actions.UpdateAddEventByDoubleClickStateAction.create(false))
            }
        }
        checkQCWaveform();
    }, [selectedWaveform, currentUserPermissions, hasAddEventPermissionOnAllWaveforms, dispatch]);

    const resetValidation = () => {
        setIsStartPositionValid(true);
        setIsEndPositionValid(true);
    }

    const listenerPoint = (draggedPoint: PeaksPoint) => {
        setStartTime(draggedPoint.timestamp);
        resetValidation();
    }

    const listenerPeriod = (draggedPeriod: PeaksPeriod) => {
        setStartTime(draggedPeriod.startTime);
        setEndTime(draggedPeriod.endTime);
        resetValidation();
    }

    React.useEffect(() => {
        setIsEditing(isEditMode !== EditModeEnum.NotEditing);

        if (isEditMode === EditModeEnum.EditingPoint) {
            setIsSegmented(false);
            setStartTime(peaksJs.waveform.activePoint.timestamp);
            resetValidation();
        } else if (isEditMode === EditModeEnum.EditingPeriod) {
            setIsSegmented(true);
            setStartTime(peaksJs.waveform.activeSegment.startTime);
            setEndTime(peaksJs.waveform.activeSegment.endTime);
            resetValidation();
        } else {
            setIsSegmented(selectedEventType?.period);
        }
    }, [peaksJs, isEditMode, selectedEventType]);

    React.useEffect(() => {
        const listener = throttle(newTime => {
            if (!isEditing) {
                setStartTime(newTime + peaksJs.options.multiDayOffset);
            }
        }, 500);

        peaksJs.on("player_load", resetValidation);
        peaksJs.on("player_play", resetValidation);
        peaksJs.on("user_seek.overview", listener);

        peaksJs.on("player_time_update", listener);

        peaksJs.on("points.dragged", listenerPoint);
        peaksJs.on("period.dragged", listenerPeriod);

        return () => {
            peaksJs.off("user_seek.overview", listener);
            peaksJs.off("player_time_update", listener);
            peaksJs.off("points.dragged", listenerPoint);
            peaksJs.off("period.dragged", listenerPeriod);
            peaksJs.off("player_load", resetValidation);
            peaksJs.off("player_play", resetValidation);
        }
    }, [peaksJs, isEditing, currentWaveformDuration]);

    const handleStartBlur = () => {
        peaksJs.player.seekBySeconds(startTime - peaksJs.options.multiDayOffset);
    }

    const handleStartChange = (timeInSeconds: number, target: HTMLInputElement & EventTarget) => {
        setIsStartPositionValid(true);

        if (target.validity.patternMismatch || !target.validity.valid) {
            setIsStartPositionValid(false);
            return;
        }

        if (isNaN(timeInSeconds)) {
            return setIsStartPositionValid(false);
        }

        setStartTime(timeInSeconds);
        if (isEditing) {
            if (isSegmented) {
                peaksJs.segments.editSegmentStart(peaksJs.waveform.activeSegment, timeInSeconds);
            } else {
                peaksJs.points.editPointTimestamp(peaksJs.waveform.activePoint, timeInSeconds);
            }

            setIsStartPositionValid(true);
        }
    };

    const handleEndChange = (timeInSeconds: number, target: HTMLInputElement & EventTarget, eventType: StudyCoughEventType) => {
        if (!eventType.period) {
            setIsEndPositionValid(true);
            return;
        } else {
            if (timeInSeconds === 0) {
                setIsEndPositionValid(false);
                return;
            }
        }

        if (target.validity.patternMismatch || !target.validity.valid) {
            setIsEndPositionValid(false);
            return;
        }

        if (isSegmented) {
            if (isNaN(timeInSeconds)) {
                return setIsEndPositionValid(false);
            } else {
                setIsEndPositionValid(true);
            }

            if (isEditing) {
                peaksJs.segments.editSegmentEnd(peaksJs.waveform.activeSegment, timeInSeconds);
            }
            setIsEndPositionValid(true);
        }

        setEndTime(timeInSeconds);

    };

    return (
        <>
            <div className="col-lg-6">
                <div className="col-lg-4 col-md-12">
                    <EventTypeSelector
                        isVisible={!isViewOnly}
                        events={availableEventTypes}
                        eventSelected={selectedEventType}
                        isEditMode={isEditMode}
                        onChange={event => {
                            if (!event.period) {
                                setIsEndPositionValid(true);
                            }

                            dispatch(
                                Actions.ChangeSelectedEventAction.create(event)
                            )
                        }
                        }
                    />&nbsp;
                </div>
                <div className="col-lg-4 col-md-12">
                    <EventAddInputs
                        isEditing={isEditing}
                        isSegmented={isSegmented}
                        startTime={startTime}
                        endTime={endTime}
                        handleStartChange={handleStartChange}
                        handleEndChange={(v, t) => handleEndChange(v, t, selectedEventType)}
                        handleStartBlur={handleStartBlur}
                        disabled={!!isPlaying}
                        isStartTimeValid={isStartPositionValid}
                        isEndTimeValid={isEndPositionValid}
                        currentWaveformDuration={currentWaveformDuration}
                        selectedSegment={selectedSegment}
                    />
                </div>
                <div className="col-lg-4 col-md-12">
                    <AddEventButton
                        isVisible={!isViewOnly}
                        isEditMode={isEditMode}
                        isPlaying={isPlaying}
                        isDisabled={!isStartPositionValid || !isEndPositionValid || !positionsLoaded || !!isAcceptabilityModalDisplayed || (!isQCWaveform && !hasAddEventPermissionOnAllWaveforms)}
                        isKeyboardShortcutVisible={areKeyboardShortcutsVisible}
                        onAddClick={() => {
                            if (!peaksJs) {
                                return;
                            }

                            if (selectedEventType.period) {
                                Actions.AddPeriodEventAsyncAction.create(
                                    {
                                        currentPosition: peaksJs.time.getCurrentTimeWithMultiDay(),
                                    },
                                    endTime > 0 ? endTime : peaksJs.time.getCurrentTimeWithMultiDay() + 3,
                                    sessionId,
                                    dispatch,
                                    coughMarshalClient,
                                    legacyClient,
                                )
                            }
                            else {
                                Actions.AddPointEventAsyncAction.create(
                                    peaksJs,
                                    {
                                        currentPosition: peaksJs.time.getCurrentTimeWithMultiDay(),
                                    },
                                    sessionId,
                                    dispatch,
                                    selectedWaveform,
                                    coughMarshalClient,
                                    legacyClient,
                                )
                            }
                        }}
                        onConfirmPointClick={() => {
                            dispatch(Actions.FinishMovePointEventAction.create(coughMarshalClient, legacyClient));
                            setIsStartPositionValid(true);
                        }
                        }
                        onConfirmPeriodClick={() => {
                            dispatch(
                                Actions.FinishMovePeriodEventAsyncAction.create(coughMarshalClient, legacyClient)
                            );
                            setIsStartPositionValid(true);
                            setIsEndPositionValid(true);
                        }}
                    />&nbsp;
                    <DeleteEventButton
                        isEditMode={isEditMode}
                        isKeyboardShortcutVisible={areKeyboardShortcutsVisible}
                        onDeletePointClick={() => {
                            dispatch(Actions.RemovePointEventAction.create(legacyClient));
                            setIsStartPositionValid(true);
                        }
                        }
                        onDeletePeriodClick={() => {
                            dispatch(Actions.RemovePeriodEventAction.create(legacyClient))
                            setIsStartPositionValid(true);
                            setIsEndPositionValid(true);
                        }
                        }
                    />
                </div>
            </div>
        </>
    );
}

function mapStateToProps(state: AnalyseState) {
    return {
        sessionId: state.core.sessionMetadata.sessionId,
        isViewOnly: state.core.isViewOnly,
        isEditMode: state.core.isEditMode,
        areKeyboardShortcutsVisible: state.core.areKeyboardShortcutsVisible,
        availableEventTypes: state.core.availableEventTypes,
        selectedEventType: state.core.selectedEventType,
        isPlaying: state.core.playState === PlayStateEnum.Playing,
        selectedWaveform: state.core.selectedWaveform,
        peaksJs: state.core.peaksJs,
        currentWaveformDuration: state.core.audioLength,
        isAcceptabilityModalDisplayed: state.core.isAcceptabilityAuditModalVisible,
        primaryAnalysisSetId: state.analysisSet.primaryAnalystSetId,
        positionsLoaded: state.core.positions ? true : false,
        selectedSegment: state.core.selectedSegment
    } as EventActionDashboardProps;
}

export default connect<EventActionDashboardProps, any, void>(mapStateToProps)(EventActionDashboard);
